import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../api";
import {
  Sparkles, Target, Gauge, Loader2, RefreshCw, TrendingUp,
  CheckCircle2, AlertTriangle, Lightbulb, GraduationCap, ArrowRight,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";
import CoeLabCardLink from "../components/CoeLabCardLink";

/* ---------------------------------------------------------------- score */
function ScoreRing({ score, size = 130 }) {
  const r = (size - 14) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score ?? 0));
  const color = pct >= 70 ? "#16A34A" : pct >= 40 ? "#006CFF" : "#EA580C";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth="10" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="10"
                strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)}
                style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-black text-3xl" data-testid="employability-score">{score ?? "—"}</div>
        <div className="text-[10px] text-muted uppercase tracking-wide">/ 100</div>
      </div>
    </div>
  );
}

function EmployabilityCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/me/career/employability").then(r => setData(r.data)).catch(() => setData(false));
  }, []);
  if (data === false) return null;
  return (
    <div className="card card-pad" data-testid="employability-card">
      <span className="eyebrow inline-flex items-center gap-1"><Gauge size={12} className="text-signal" /> Employability score</span>
      {data === null ? (
        <div className="mt-4 text-muted text-sm">Computing…</div>
      ) : (
        <div className="mt-4 flex flex-col sm:flex-row gap-6 items-center">
          <ScoreRing score={data.score} />
          <div className="flex-1 w-full space-y-2">
            {data.breakdown.map(b => (
              <div key={b.key} data-testid={`emp-breakdown-${b.key}`}>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">{b.label}</span>
                  <span className="font-mono font-semibold">{b.score}/{b.max}</span>
                </div>
                <div className="h-1.5 bg-rule rounded-full mt-1 overflow-hidden">
                  <div className="h-full rounded-full bg-signal transition-all duration-700"
                       style={{ width: `${(b.score / b.max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-[11px] text-muted mt-4">
        Auto-recalculated on every milestone — profile edits, resume uploads, lab completions, assessments and applications.
      </p>
    </div>
  );
}

/* ------------------------------------------------------- profile analysis */
function ProfileAnalysisCard() {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/me/career/profile-analysis").then(r => setA(r.data.analysis)).catch(() => {});
  }, []);
  const run = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/me/career/profile-analysis");
      setA(r.data.analysis);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="card card-pad" data-testid="profile-analysis-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="eyebrow inline-flex items-center gap-1"><Sparkles size={12} className="text-signal" /> AI profile analysis</span>
          <h3 className="font-display font-bold text-xl mt-1">ATS score & recruiter readiness</h3>
        </div>
        <button className="btn-primary" onClick={run} disabled={busy} data-testid="run-profile-analysis-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : a ? <RefreshCw size={14} /> : <Sparkles size={14} />}
          {busy ? "Analyzing…" : a ? "Re-analyze" : "Analyze my profile"}
        </button>
      </div>
      {err && <div className="text-xs text-red-600 mt-3" data-testid="profile-analysis-err">{err}</div>}
      {!a && !busy && !err && (
        <p className="text-muted text-sm mt-4" data-testid="no-analysis">
          Let AI review your profile, resume, projects and CoE credentials — get an ATS score, strengths, gaps and career matches.
        </p>
      )}
      {a && (
        <div className="mt-5 space-y-5" data-testid="analysis-result">
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <div className="border border-rule rounded-xl p-3 text-center">
              <div className="font-display font-black text-3xl text-signal" data-testid="ats-score">{a.ats_score}</div>
              <div className="text-[11px] text-muted uppercase tracking-wide mt-1">ATS score</div>
            </div>
            <div className="border border-rule rounded-xl p-3 text-center">
              <div className="font-display font-black text-3xl" style={{ color: "#9933FF" }} data-testid="profile-score">{a.profile_score}</div>
              <div className="text-[11px] text-muted uppercase tracking-wide mt-1">Profile score</div>
            </div>
          </div>
          {a.summary && <p className="text-sm text-muted italic">{a.summary}</p>}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs font-semibold inline-flex items-center gap-1 text-green-700"><CheckCircle2 size={13} /> Strengths</div>
              <ul className="mt-2 space-y-1.5">{(a.strengths || []).map(s => <li key={s} className="text-sm text-muted flex gap-2"><span className="text-green-600 mt-0.5">•</span>{s}</li>)}</ul>
            </div>
            <div>
              <div className="text-xs font-semibold inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={13} /> Gaps</div>
              <ul className="mt-2 space-y-1.5">{(a.weaknesses || []).map(s => <li key={s} className="text-sm text-muted flex gap-2"><span className="text-amber-600 mt-0.5">•</span>{s}</li>)}</ul>
            </div>
            <div>
              <div className="text-xs font-semibold inline-flex items-center gap-1 text-signal"><Lightbulb size={13} /> Improve next</div>
              <ul className="mt-2 space-y-1.5">{(a.improvements || []).map(s => <li key={s} className="text-sm text-muted flex gap-2"><span className="text-signal mt-0.5">•</span>{s}</li>)}</ul>
            </div>
          </div>
          {(a.career_recommendations || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold inline-flex items-center gap-1"><TrendingUp size={13} className="text-signal" /> AI career recommendations</div>
              <div className="mt-2 grid md:grid-cols-3 gap-3">
                {a.career_recommendations.map(r => (
                  <div key={r.role} className="border border-rule rounded-xl p-3" data-testid={`career-rec-${r.role}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display font-bold text-sm">{r.role}</div>
                      <span className="text-xs font-bold text-signal shrink-0">{r.match_pct}%</span>
                    </div>
                    <p className="text-xs text-muted mt-1">{r.why}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ skill gap */
const PRIORITY_STYLE = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-blue-50 text-blue-700 border-blue-200",
};

function SkillGapCard() {
  const [gap, setGap] = useState(null);
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/me/career/skill-gap").then(r => {
      setGap(r.data.gap);
      if (r.data.gap?.target_role) setRole(r.data.gap.target_role);
    }).catch(() => {});
  }, []);
  const run = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post("/me/career/skill-gap", { target_role: role });
      setGap(r.data.gap);
    } catch (e2) { setErr(formatError(e2)); }
    finally { setBusy(false); }
  };
  return (
    <div className="card card-pad" data-testid="skill-gap-card">
      <span className="eyebrow inline-flex items-center gap-1"><Target size={12} className="text-signal" /> AI skill gap analysis</span>
      <h3 className="font-display font-bold text-xl mt-1">How ready are you for your dream role?</h3>
      <form onSubmit={run} className="mt-4 flex gap-2 flex-wrap">
        <input className="input flex-1 min-w-[220px]" placeholder="Target role — e.g. Embedded Systems Engineer"
               value={role} onChange={e => setRole(e.target.value)} data-testid="skill-gap-role-input" />
        <button className="btn-primary" disabled={busy || role.trim().length < 3} data-testid="run-skill-gap-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Target size={14} />}
          {busy ? "Analyzing…" : "Analyze gap"}
        </button>
      </form>
      {err && <div className="text-xs text-red-600 mt-3" data-testid="skill-gap-err">{err}</div>}
      {gap && (
        <div className="mt-5 space-y-5" data-testid="skill-gap-result">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="border border-rule rounded-xl px-5 py-3 text-center">
              <div className="font-display font-black text-3xl text-signal" data-testid="readiness-pct">{gap.readiness_pct}%</div>
              <div className="text-[11px] text-muted uppercase tracking-wide">Role readiness</div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-display font-bold">{gap.target_role}</div>
              {gap.market_demand && <p className="text-xs text-muted mt-1">{gap.market_demand}</p>}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold text-green-700 mb-2">✓ Skills you already have</div>
              <div className="flex flex-wrap gap-1.5">
                {(gap.matched_skills || []).map(s => (
                  <span key={s} className="text-xs px-2 py-1 rounded-full border bg-green-50 text-green-700 border-green-200">{s}</span>
                ))}
                {(gap.matched_skills || []).length === 0 && <span className="text-xs text-muted">None matched yet</span>}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-red-700 mb-2">Missing skills</div>
              <div className="space-y-1.5">
                {(gap.missing_skills || []).map(m => (
                  <div key={m.skill} className="flex items-start gap-2" data-testid={`missing-skill-${m.skill}`}>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border shrink-0 ${PRIORITY_STYLE[m.priority] || PRIORITY_STYLE.low}`}>{m.priority}</span>
                    <div className="text-sm min-w-0"><span className="font-semibold">{m.skill}</span>
                      <span className="text-muted text-xs"> — {m.why}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {(gap.recommended_labs || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold inline-flex items-center gap-1 mb-2"><GraduationCap size={13} className="text-signal" /> CoE labs that close your gap</div>
              <div className="grid sm:grid-cols-3 gap-2">
                {gap.recommended_labs.map(lb => (
                  <CoeLabCardLink key={lb.id}
                                  labCode={lb.code}
                                  fallbackInternalHref={`/lms/labs/${lb.id}`}
                                  className="border border-rule rounded-xl p-3 hover:border-signal transition group block"
                                  testid={`rec-lab-${lb.code}`}>
                    <div className="font-mono text-xs text-signal">{lb.code}</div>
                    <div className="font-display font-bold text-sm mt-0.5 group-hover:text-signal transition">{lb.name}</div>
                  </CoeLabCardLink>
                ))}
              </div>
            </div>
          )}
          {(gap.learning_path || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2">Your learning path</div>
              <ol className="space-y-2">
                {gap.learning_path.map((step, i) => (
                  <li key={step} className="flex gap-3 text-sm">
                    <span className="h-6 w-6 rounded-full bg-signal/10 text-signal font-bold text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                    <span className="text-muted">{step}</span>
                  </li>
                ))}
              </ol>
              <Link to="/student/lms" className="btn-secondary mt-4 inline-flex">
                Start learning <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ page */
export default function StudentCareer() {
  usePageTitle("Career AI");
  return (
    <div className="space-y-6" data-testid="career-ai-page">
      <EmployabilityCard />
      <ProfileAnalysisCard />
      <SkillGapCard />
    </div>
  );
}
